import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { formatCurrency } from 'Utils/formatters';
import Tooltip from 'Components/Molecules/Tooltip/Tooltip';
import Icon from 'Components/Atoms/Icon/Icon';
import useTransaction from 'Hooks/useTransaction';
import Loading from 'Components/Atoms/Loading/Loading';
import TransactionSplitEditorDrawerSlice from './TransactionSplitEditorDrawerSlice';
import { splitTransaction, deleteTransactionSlices } from 'Services/transactions';
import { useErrors } from 'Components/Organisms/ErrorsProvider';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import useLocale from 'Hooks/useLocale';
import Button from 'Components/Atoms/Button/Button';
import { cn } from 'Utils/cn';
import ErrorMessage from 'Components/Atoms/ErrorMessage/ErrorMessage';
import FormProvider from 'Components/Organisms/HookForm';

export default function TransactionSplitEditorDrawer(props) {
    const { transactionId, onClose, onCancel } = props;

    const { t } = useTranslation('transactions');
    const locale = useLocale();
    const { transaction, isLoading } = useTransaction(transactionId);
    const [errorMessage, setErrorMessage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const { showUnknownError } = useErrors();

    const queryClient = useQueryClient();
    const initializedRef = useRef(false);

    const { provider_connection_product, description, children_transactions, amount } = transaction;

    const isAlreadySplit = Array.isArray(children_transactions) && children_transactions.length > 0;

    const schema = useMemo(
        () =>
            yup.object({
                slices: yup.array().of(
                    yup.object({
                        child_transaction_id: yup.string().nullable(),
                        description: yup.string().trim().required(t('concept-cannot-be-empty')),
                        amount: yup
                            .number()
                            .transform((value, originalValue) => (originalValue === '' ? NaN : value))
                            .typeError(t('common:field-required'))
                            .required(t('common:field-required')),
                    }),
                ),
            }),
        [t],
    );

    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues: { slices: [] },
        mode: 'onSubmit',
    });

    const { control, handleSubmit, reset, setFocus } = methods;

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'slices',
    });

    const watchedSlices = useWatch({ control, name: 'slices' });

    useEffect(() => {
        if (typeof amount !== 'number') return;

        if (!initializedRef.current) {
            if (isAlreadySplit) {
                reset({
                    slices: children_transactions.map((c) => ({
                        child_transaction_id: c.id,
                        description: c.description ?? '',
                        amount: c.amount ?? 0,
                    })),
                });
            } else {
                const half = Number((amount / 2).toFixed(2));
                const remainder = Number((amount - half).toFixed(2));
                reset({
                    slices: [
                        { child_transaction_id: null, description: '', amount: half },
                        { child_transaction_id: null, description: '', amount: remainder },
                    ],
                });
            }
            initializedRef.current = true;
            return;
        }

        if (fields.length === 0) {
            setTimeout(() => {
                append({ child_transaction_id: null, description: '', amount });
            }, 500);
        }
    }, [children_transactions, amount, isAlreadySplit, reset, append, fields.length]);

    const isExpense = amount * (transaction.reverse_sign ? -1 : 1) < 0;

    const rest = useMemo(() => {
        const sliceTotals = (watchedSlices || []).reduce((acc, slice) => {
            const sliceAmount = Number(slice?.amount);
            return acc + (Number.isFinite(sliceAmount) ? sliceAmount : 0);
        }, 0);
        return Number(((amount ?? 0) - sliceTotals).toFixed(2));
    }, [watchedSlices, amount]);

    useEffect(() => {
        setErrorMessage((current) => (current ? null : current));
    }, [watchedSlices]);

    const handleAddSlice = () => {
        append({ child_transaction_id: null, description: '', amount: rest });
        const newIndex = fields.length;
        setTimeout(() => setFocus(`slices.${newIndex}.description`), 0);
    };

    const handleRemoveSlices = async () => {
        setDeleting(true);
        try {
            await deleteTransactionSlices(transactionId);
            queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            queryClient.invalidateQueries({ queryKey: ['transactions'] });

            onClose();
        } catch (error) {
            showUnknownError(error);
            onClose();
        } finally {
            setDeleting(false);
        }
    };

    const onValid = async (data) => {
        if (rest !== 0) {
            setErrorMessage(t('the-sum-must-equal-the-amount-of-the-original-transaction'));
            return;
        }

        if (data.slices.length <= 1) {
            setErrorMessage(t('there-must-be-at-least-2-transactions'));
            return;
        }

        const slicesNormalized = data.slices.map((slice) => ({
            child_transaction_id: slice.child_transaction_id || null,
            description: slice.description,
            amount: slice.amount,
        }));

        setLoading(true);
        try {
            await splitTransaction(transactionId, slicesNormalized);
            queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            queryClient.invalidateQueries({ queryKey: ['transactions'] });

            onClose();
        } catch (error) {
            showUnknownError(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col h-full">
            {isLoading && (
                <div className="py-6 flex justify-center">
                    <Loading />
                </div>
            )}

            {!isLoading && (
                <FormProvider methods={methods} onSubmit={handleSubmit(onValid)} className="flex flex-col h-full">
                    <div className="mb-6 pt-4">
                        <div className="flex items-center gap-1.5 mb-1">
                            <img src={provider_connection_product?.provider_logo_url} width={20} height={20} alt="" />
                            <div>
                                <div className="font-semibold text-sm">{provider_connection_product?.name}</div>
                            </div>
                        </div>

                        <div className="flex justify-between items-start gap-3 text-lg mb-2 font-semibold text-dark-blue">
                            <div>{description}</div>
                            <div
                                className={cn(
                                    'flex justify-end gap-0.5 items-center font-mono font-medium',
                                    !isExpense && 'text-income',
                                )}
                            >
                                {transaction.reverse_sign && (
                                    <Tooltip label={t('modified-sign')}>
                                        <span className="flex">
                                            <Icon name="reverse" size={16} color="currentColor" className="mr-1" />
                                        </span>
                                    </Tooltip>
                                )}
                                {formatCurrency(amount, {
                                    currency: transaction.currency,
                                    locale,
                                })}
                            </div>
                        </div>

                        {isAlreadySplit && (
                            <div className="py-2">
                                <Button type="button" isBusy={deleting} size="extraSmall" onClick={handleRemoveSlices}>
                                    <Icon name="undo" size={16} color="currentColor" />
                                    {t('undo-split')}
                                </Button>
                            </div>
                        )}

                        {errorMessage && (
                            <div className="px-3 py-4 mt-2 bg-red-100 rounded-md">
                                <ErrorMessage message={errorMessage} />
                            </div>
                        )}

                        <div className="pt-2">
                            <div className="grid mb-2 pb-2 border-b border-gray-200">
                                {fields.map((field, index) => (
                                    <TransactionSplitEditorDrawerSlice
                                        currency={transaction.currency}
                                        key={field.id}
                                        name={`slices.${index}`}
                                        autoFocus={index === 0 && !isAlreadySplit}
                                        onDelete={() => remove(index)}
                                    />
                                ))}
                            </div>

                            <div className="flex justify-between items-center py-1 text-xs">
                                <button
                                    className="inline-flex items-center text-gray-500 gap-1 font-semibold text-left py-1 px-0"
                                    type="button"
                                    onClick={handleAddSlice}
                                >
                                    <Icon name="plus" color="currentColor" size={14} />
                                    <span>{t('add-transaction')}</span>
                                </button>
                                <div
                                    className={cn('text-right font-medium', rest === 0 ? 'text-income' : 'text-danger')}
                                >
                                    {t('remaining')}:{' '}
                                    {formatCurrency(rest, {
                                        currency: transaction.currency,
                                        locale,
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grow" />

                    <div className="sticky bottom-0 bg-white pt-2 pb-4 z-10">
                        <div className="flex gap-2">
                            <Button
                                isBusy={loading}
                                variant="primary"
                                type="submit"
                                className="grow"
                                testId="transaction-split-editor.split"
                            >
                                {t('split')}
                            </Button>
                            <Button
                                type="button"
                                variant="default"
                                className="grow"
                                onClick={onCancel}
                                testId="transaction-split-editor.cancel"
                            >
                                {t('cancel')}
                            </Button>
                        </div>
                    </div>
                </FormProvider>
            )}
        </div>
    );
}

TransactionSplitEditorDrawer.propTypes = {
    transactionId: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onSubmit: PropTypes.func,
};

TransactionSplitEditorDrawer.drawerStackProps = {
    size: 'lg',
};
